/* DEV-291 v11 (2026-09-26): endless "You may also like" feed on the home page, as in the approved design
   (artifact TmL8pRYadWK9tFpJ2JFPth: 12 products at a time, prefetch when 600 px are left, a theme card after each batch,
   on phone and desktop alike). Same idea as assets/lm-collection.js (DEV-288): the next page of the section is fetched
   through the Section Rendering API (/<locale>/?section_id=<section>&page=N), its cards are appended without repeats and
   the feed stops at the end of the collection, where the "Show more" link to the whole collection comes back.
   Cards need no extra set-up: lm-card.js binds every product-card added to the page (swipe, dots, heart, "+").
   v12 (2026-09-27, DEV-394): products already shown in the home shelves (server-side skip list in the section, plus any
   shelf cards in the page, including ones the shelves loaded while swiping) are never added to the feed. */
(() => {
  const started = new WeakSet();

  const init = (root) => {
    if (!root || started.has(root)) return;
    const grid = root.querySelector('.d291-fy__grid');
    const loader = root.querySelector('.d291-fy__load');
    const pages = parseInt(root.dataset.pages, 10) || 1;
    let last = parseInt(root.dataset.page, 10) || 1;
    if (!grid || !loader || last >= pages || !window.fetch || !window.DOMParser) return;
    started.add(root);

    const base = (window.Shopify && Shopify.routes && Shopify.routes.root) || '/';
    let loading = false;
    let stopped = false;
    let io = null;
    let frame = 0;

    /* DEV-394: also skip what the home shelves show (incl. cards they loaded while swiping) – no product twice on the page */
    const handles = () => new Set(Array.from(document.querySelectorAll('.d291-fy__grid product-card[handle], [data-d291-shelf] product-card[handle]')).map((card) => card.getAttribute('handle')));

    const finish = () => {
      stopped = true;
      if (io) io.disconnect();
      window.removeEventListener('scroll', check);
      window.removeEventListener('resize', check);
      root.classList.add('d291-fy--end');
      grid.removeAttribute('aria-busy');
    };

    const pageItems = async (n) => {
      const url = new URL(base, location.origin);
      url.searchParams.set('section_id', root.dataset.section);
      url.searchParams.set('page', n);
      const response = await fetch(url.toString(), { credentials: 'same-origin' });
      if (!response.ok) throw new Error(response.status);
      const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
      const other = doc.querySelector('[data-d291-feed]');
      if (other && (parseInt(other.dataset.page, 10) || 0) !== n) return null; /* past the last page Shopify serves the last one again */
      return Array.from(doc.querySelectorAll('.d291-fy__grid > li'));
    };

    const append = async () => {
      if (loading || stopped) return;
      loading = true;
      grid.setAttribute('aria-busy', 'true');
      try {
        const items = await pageItems(last + 1);
        if (stopped) return;
        last += 1;
        if (items) {
          const seen = handles();
          let added = 0;
          const nodes = [];
          items.forEach((item) => {
            const card = item.querySelector('product-card[handle]');
            if (card) {
              const handle = card.getAttribute('handle');
              if (seen.has(handle)) return;
              seen.add(handle);
              added += 1;
            } else if (!added) {
              return; /* no theme card right after a batch that brought nothing new */
            }
            const node = document.importNode(item, true);
            node.querySelectorAll('[reveal-js]').forEach((el) => el.removeAttribute('reveal-js'));
            node.querySelectorAll('script').forEach((el) => el.remove());
            nodes.push(node);
          });
          grid.append(...nodes);
        }
        root.dataset.page = String(last);
        if (last >= pages) finish();
      } catch (error) {
        finish(); /* the link to the whole collection is shown instead */
      } finally {
        loading = false;
        if (!stopped) grid.removeAttribute('aria-busy');
      }
      if (!stopped) check();
    };

    /* a scroll check as well as the observer: a fast fling can jump past the loader without it ever intersecting */
    function check() {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(() => {
        if (!stopped && loader.isConnected && loader.getBoundingClientRect().top < window.innerHeight + 600) append();
      });
    }

    root.classList.add('d291-fy--feed');
    if ('IntersectionObserver' in window) {
      io = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) append();
      }, { rootMargin: '0px 0px 600px 0px' });
      io.observe(loader);
    }
    window.addEventListener('scroll', check, { passive: true });
    window.addEventListener('resize', check, { passive: true });
    check();
  };

  const boot = () => document.querySelectorAll('[data-d291-feed]').forEach(init);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', boot);
})();
